import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { swallow } from "@/lib/log";
import { cn } from "@/lib/utils";
import {
  Clock, StickyNote, Bell, FileText, KeyRound, Image as ImageIcon, Film, BookText, Pin,
} from "lucide-react";

const TYPE_META = {
  note:     { icon: StickyNote, to: "/app/notes",              color: "text-amber-500 bg-amber-500/12"     },
  reminder: { icon: Bell,       to: "/app/reminders",          color: "text-orange-500 bg-orange-500/12"   },
  document: { icon: FileText,   to: "/app/documents",          color: "text-emerald-500 bg-emerald-500/12" },
  password: { icon: KeyRound,   to: "/app/passwords",          color: "text-indigo-500 bg-indigo-500/12"   },
  photo:    { icon: ImageIcon,  to: "/app/media?tab=photos",   color: "text-purple-500 bg-purple-500/12"   },
  video:    { icon: Film,       to: "/app/media?tab=videos",   color: "text-pink-500 bg-pink-500/12"       },
  journal:  { icon: BookText,   to: "/app/journal",            color: "text-rose-500 bg-rose-500/12"       },
};

/**
 * Compact horizontal strip shown at the top of the Chat/Home page.
 * Fetches /pins (pinned items) first; if none, falls back to a small "recent"
 * assembled from the same endpoint set as Library. Hidden when nothing to show.
 * Feels like a "Recents" iOS shortcut, keeps the promise: "your recent things
 * are one tap away, above your chat".
 */
export default function ChatRecentStrip() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: pins } = await api.get("/pins");
        if (cancelled) return;
        if (pins && pins.length) { setItems(pins.slice(0, 8)); setLoaded(true); return; }
        // Fallback: aggregate a lightweight recent feed
        const [notes, docs, rems] = await Promise.all([
          api.get("/notes").catch(() => ({ data: [] })),
          api.get("/documents").catch(() => ({ data: [] })),
          api.get("/reminders").catch(() => ({ data: [] })),
        ]);
        const feed = [];
        (notes.data || []).slice(0, 5).forEach((n) => feed.push({ type: "note", id: n.id, title: n.title || (n.content || "").slice(0, 40) || "Note", updated: n.updated_at }));
        (docs.data  || []).slice(0, 3).forEach((d) => feed.push({ type: "document", id: d.id, title: d.title || "Document", updated: d.updated_at, file_url: d.file_url, mime_type: d.mime_type }));
        (rems.data  || []).slice(0, 3).forEach((r) => feed.push({ type: "reminder", id: r.id, title: r.text || "Reminder", updated: r.updated_at }));
        feed.sort((a, b) => new Date(b.updated || 0) - new Date(a.updated || 0));
        setItems(feed.slice(0, 6));
      } catch (e) { swallow("ChatRecentStrip")(e); }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;
  if (!items.length) return null;

  return (
    <div className="-mx-4 px-4 pb-2" data-testid="chat-recent-strip">
      <div className="flex items-center justify-between mb-1.5 px-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1.5">
          <Clock className="h-3 w-3" /> Recent
        </div>
      </div>
      <div
        className="flex gap-2 overflow-x-auto no-scrollbar pb-1"
        style={{ scrollSnapType: "x mandatory" }}
      >
        {items.map((it) => {
          const meta = TYPE_META[it.type] || TYPE_META.note;
          const Icon = meta.icon;
          const isMedia = it.type === "photo" || it.type === "video";
          return (
            <button
              key={`${it.type}-${it.id}`}
              type="button"
              onClick={() => nav(meta.to)}
              className={cn(
                "shrink-0 w-[128px] rounded-2xl border border-border bg-card text-left overflow-hidden",
                "hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition active:scale-[0.98]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              )}
              style={{ scrollSnapAlign: "start" }}
              data-testid={`recent-${it.type}-${it.id}`}
              aria-label={`${it.type}: ${it.title}`}
            >
              {isMedia && it.file_url ? (
                <div className="aspect-[5/3] w-full bg-muted overflow-hidden">
                  <img
                    src={fileUrl(it.file_url)}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
              ) : (
                <div className={cn("aspect-[5/3] w-full grid place-items-center", meta.color)}>
                  <Icon className="h-5 w-5" strokeWidth={1.9} />
                </div>
              )}
              <div className="p-2">
                <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground truncate flex items-center gap-1">
                  {it.pinned_at && <Pin className="h-2.5 w-2.5 fill-current text-primary" />}
                  {it.type}
                </div>
                <div className="text-[11px] font-semibold leading-tight mt-0.5 line-clamp-2">
                  {it.title}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
